import cube from "@cubejs-client/core";
import { EnvironmentEnum } from "@lyfegen-platform/graphql-schema/models";
import { newLyfeTheme } from "@lyfegen-platform/react-common";
import { ThemeProvider } from "@material-ui/core";
import * as Sentry from "@sentry/react";
import TimeAgo from "javascript-time-ago";
import de from "javascript-time-ago/locale/de.json";
import en from "javascript-time-ago/locale/en.json";
import ReactDOM from "react-dom";
import TagManager from "react-gtm-module";
import { RecoilRoot } from "recoil";
import App from "./App";
import MaintenancePage from "./components/MaintenancePage";
import { SENTRY_ENV_NOT_SET } from "./config";
import { AuthenticationService } from "./services/authentication.service";
import uiVersion from "./ui-version";
import { checkSentryUserPreference } from "./utils/check-sentry-accept";
import { cyrb53 } from "./utils/hash";

fetch("/api/config")
	.then(async (response: Response) => {
		const { identityServerUrl, ui, translationsPath, serverEnvironment, gtmId, navlinUrl, sentryEnv, sentryAppVersion } =
			await response.json();
		uiVersion.setVersion(ui);
		const authenticationService = new AuthenticationService(identityServerUrl);
		const cubeApi = cube(
			async () => {
				const user = await authenticationService.getUser();

				return user?.access_token || "";
			},
			{ apiUrl: "/cubejs-api/v1" }
		);

		const user = await authenticationService.getUserProfile();

		function handleLogout() {
			authenticationService.startSignOut();
			sessionStorage.clear();
		}

		TimeAgo.addDefaultLocale(en);
		TimeAgo.addLocale(de);

		if (checkSentryUserPreference((window as any).dataLayer)) {
			if (process.env.SENTRY_ENV) {
				Sentry.init({
					dsn: "https://67ac49632c7131b7a6d6d6b5e6def4d1@o4505907510181888.ingest.sentry.io/4506155796529152",
					integrations: [
						Sentry.browserTracingIntegration(),
						Sentry.rewriteFramesIntegration({
							root: "/home/site/wwwroot/",
							prefix: "/client/",
						}),
					],
					tracePropagationTargets: ["platform.lyfegen.com", "app.lyfegen.com", "dev.lyfegen.com"],
					release: `platform-client@${sentryAppVersion.version}`,
					environment: sentryEnv || SENTRY_ENV_NOT_SET,
					tracesSampleRate: 0.3,
					replaysSessionSampleRate: 0,
					replaysOnErrorSampleRate: 1,
					beforeSend(event) {
						if (event.request?.url?.includes("localhost")) {
							return null;
						}
						event.tags = {
							...event.tags,
							location: "client",
						};

						return event;
					},
				});
			}
		}

		if ([EnvironmentEnum.Production, EnvironmentEnum.Dev, EnvironmentEnum.Demo].includes(serverEnvironment)) {
			if (serverEnvironment !== EnvironmentEnum.Demo) {
				const consentSharingDomain =
					serverEnvironment === EnvironmentEnum.Production ? "https://auth.lyfegen.com" : "https://auth-dev.lyfegen.com";
				// @ts-ignore
				window.UC_UI_DOMAINS = {
					crossDomainConsentSharingIFrame: `${consentSharingDomain}/cross-domain-bridge.html`,
				};
			}
			const tagManagerArgs = {
				gtmId,
				dataLayer: {
					userEmail: user.email,
					organization: user.organization?.name,
					hashUserId: user.id ? cyrb53(user.id).toString() : "",
				},
			};

			TagManager.initialize(tagManagerArgs);
		}

		ReactDOM.render(
			<RecoilRoot>
				<App
					user={user}
					handleLogout={handleLogout}
					identityServerUrl={identityServerUrl}
					authService={authenticationService}
					cubeApi={cubeApi}
					translationsPath={translationsPath}
					serverEnvironment={serverEnvironment}
					navlinUrl={navlinUrl}
					sentryEnv={sentryEnv || SENTRY_ENV_NOT_SET}
				/>
			</RecoilRoot>,
			document.getElementById("root")
		);
	})
	.catch((error) => {
		if (error.error === "login_required") {
			ReactDOM.render(<>Redirecting to login app...</>, document.getElementById("root"));
		} else {
			console.error(JSON.stringify(error));
			ReactDOM.render(
				<ThemeProvider theme={newLyfeTheme}>
					<MaintenancePage />
				</ThemeProvider>,
				document.getElementById("root")
			);
		}
	});
